import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import commonColDef from '../../../data/grid/commonColDef';
import useGridOptionsConditionHistory from '../../../hooks/grid/gridOptions/useGridOptionsConditionHistory';
import useColdefManageApproval from '../../../hooks/grid/coldef/useColdefManageApproval';
import OrganizationIdCellRenderer from '../../cellRenderers/OrganizationIdCellRenderer';
import SteelMakerCellRenderer from '../../cellRenderers/SteelMakerCellRenderer';
import BusinessTypeCellRenderer from '../../cellRenderers/BusinessTypeCellRenderer';
import SteelCategoryCellRenderer from '../../cellRenderers/SteelCategoryCellRenderer';
import ConditionStatusRenderer from '../../cellRenderers/ConditionStatusRenderer';
import { useTranslation } from 'react-i18next';
import useLocales from '../../../hooks/useLocales';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { openDialog, getConditionHistoryList, getConfirmCondition } from '../../../redux/slices/approval';
import type { ColDef, ColumnApi, GridApi, GridReadyEvent, RowDoubleClickedEvent } from 'ag-grid-community';
import type { ApproveType } from '../../../types/approval';
import type { ConditionWithDetailType } from '../../../types/condition';
import type { OptionsType } from '../../../types/options';
import type { BusinessType, SteelCategory, SteelMaker } from '../../../types/common';
import type { AdminPowerUsers } from '../../../types/user';

interface ConditionHistoryGridProps {
  isLoading: boolean;
  selectedOrganizationId: number | undefined;
  conditionHistory: ApproveType[] | undefined;
  confirmCondition: ConditionWithDetailType | undefined;
  steelMaker: OptionsType<SteelMaker>[];
  businessType: OptionsType<BusinessType>[];
  steelCategory: OptionsType<SteelCategory>[];
  adminPowerUsers: AdminPowerUsers | undefined;
}

const ConditionHistoryGrid = ({
  isLoading,
  selectedOrganizationId,
  conditionHistory,
  confirmCondition,
  steelMaker,
  businessType,
  steelCategory,
  adminPowerUsers,
}: ConditionHistoryGridProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { currentLang } = useLocales();
  const coldefManageApproval = useColdefManageApproval(t);
  const { email } = useSelector((state: RootState) => state.session);
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | undefined>(undefined);
  const { gridOptions } = useGridOptionsConditionHistory();
  const customColDef: ColDef = {
    cellClass: 'cell',
    flex: 1,
  };
  const defaultColDef: ColDef = Object.assign({}, commonColDef, customColDef);
  const columnDefs: ColDef[] = coldefManageApproval;
  const frameworkComponents = {
    organizationIdCellRenderer: OrganizationIdCellRenderer,
    steelMakerCellRenderer: SteelMakerCellRenderer,
    businessTypeCellRenderer: BusinessTypeCellRenderer,
    steelCategoryCellRenderer: SteelCategoryCellRenderer,
    conditionStatusRenderer: ConditionStatusRenderer,
  };

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  /**
   * Loading, NoRowsのグリッド表示
   */
  useEffect(() => {
    if (gridApi) {
      if (isLoading) {
        gridApi.showLoadingOverlay();
      } else {
        if (conditionHistory?.length) {
          gridApi.hideOverlay();
        } else {
          gridApi.showNoRowsOverlay();
        }
      }
    } else {
      // do nothing
    }
  }, [gridApi, isLoading, conditionHistory]);

  // /**
  //  * ステータスが変わったらconditionを再取得する
  //  */
  useEffect(() => {
    if (confirmCondition) {
      dispatch(getConditionHistoryList(selectedOrganizationId));
    } else {
      // do nothing
    }
  }, [confirmCondition?.conditionStatus]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * グリッドが準備できたらAPIを利用できるようにする
   * @param params GridReadyEvent
   */
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  /**
   * ダブルクリックで編集モードにする
   * @param params GridReadyEvent
   */
  const onRowDoubleClicked = (event: RowDoubleClickedEvent) => {
    // console.log(event);
    const data: ApproveType = event.data;
    if (data.conditionStatus === 'REQUEST' && email === data.approverEmail) {
      const data: ApproveType = event.data;
      dispatch(getConfirmCondition(data.id));
      dispatch(openDialog());
    }
  };

  // VIEW
  // ================================================================================

  return (
    <>
      <div className="ag-theme-balham" style={{ height: '70vh' }}>
        <AgGridReact
          immutableData={true}
          gridOptions={gridOptions}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={conditionHistory ?? []}
          onGridReady={onGridReady}
          onRowDoubleClicked={onRowDoubleClicked}
          frameworkComponents={frameworkComponents}
          // filterValueGetterで使うデータ
          context={{
            currentLang,
            // reduxにあるものはコピーを渡す
            steelMaker: steelMaker?.map((maker) => structuredClone(maker)),
            businessType: businessType?.map((business) => structuredClone(business)),
            steelCategory: steelCategory?.map((category) => structuredClone(category)),
            adminPowerUsers: adminPowerUsers ? structuredClone(adminPowerUsers) : undefined,
          }}
        />
      </div>
    </>
  );
};

export default ConditionHistoryGrid;
